import React, { useCallback, useEffect, useRef, useState } from "react";
import { SortType } from "../../../../../models/SortType";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import dayjs from "dayjs";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { PurhcaseRequestFilters } from "../../../../../models/PurchaseRequest/PurchaseRequestDetails";
import PrListingTable from "../../components/purchase-request/Prlisting";
import { PurchaseRequestRejectedBy } from "../../../../../models/PurchaseRequest/PurchaseRequestBody";
import {
  PurchaseRequestApprovalTeam,
  PurchaseRequestApprovedType,
  PurchaseRequestPendingApprovalType,
  PURCHASE_REQUEST_APPROVED_OPTIONS,
  PURCHASE_REQUEST_PENDING_APPROVAL_OPTIONS,
  parsePurchaseRequestApprovedTypes,
  parsePurchaseRequestPendingApprovalTypes,
} from "../../../../../models/PurchaseRequest/PurchaseRequestApprovalType";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import {
  modernFilterPanelHeaderSx,
  modernFilterPanelSx,
  modernPageCardSx,
} from "../../../../../styles/modernListingPageSx";
import { Admin } from "../../../../../models/TaskTracker/Admin";
import { VendorPaymentsStore } from "../../../stores/VendorPaymentsStore";
import VendorPaymentsCsvUploadDialog from "../../components/purchase-request/VendorPaymentsCsvUploadDialog";

const vendorPaymentsListingStore = new VendorPaymentsStore();

const PurchaseRequest: React.FC = () => {
  const theme = useTheme();
  const {
    userStore,
    purchaseRequestStore,
    orderStore,
    TaskTrackerStore,
  } = useStores();
  const { showError, showSuccess } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const adminSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const orderSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [approvePopup, setApprovePopup] = useState<{
    open: boolean;
    prId: number | null;
    team: PurchaseRequestRejectedBy | null;
  }>({ open: false, prId: null, team: null });
  const [rejectPopup, setRejectPopup] = useState<{
    open: boolean;
    prId: number | null;
    team: PurchaseRequestRejectedBy | null;
  }>({ open: false, prId: null, team: null });
  const [cancelPRPopup, setCancelPRPopup] = useState<{
    open: boolean;
    prId: number | null;
  }>({ open: false, prId: null });
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [vendorPaymentsCsvOpen, setVendorPaymentsCsvOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const filterFields: (keyof PurhcaseRequestFilters)[] = [
    "pr_created_by_admin_ids",
    "order_id",
    "created_date_from",
    "expected_payment_date_from",
    "pending_approval_type",
    "approved_type",
    "pr_status",
    "urgent_payment",
  ];

  const PR_STATUS_OPTIONS: FilterValue[] = [
    { id: "1", value: "Active" },
    { id: "0", value: "Cancelled" },
    { id: "2", value: "Rejected" },
  ];

  const canViewPrPo = userStore.hasPermission([
    EmployeeRoles.PR_PO_VIEW,
    EmployeeRoles.CREATE_PR_AND_PO,
  ]);

  const canUploadVendorPaymentsCsv = userStore.hasPermission(
    EmployeeRoles.UPLOAD_VENDOR_PAYMENTS_CSV,
  );

  const URGENT_PAYMENT_FILTER_OPTIONS: FilterValue[] = [
    { id: "not_required", value: "Not required" },
    { id: "pending", value: "Urgent payment pending" },
    { id: "done", value: "Urgent payment Paid" },
  ];

  const parseUrlParams = (): PurhcaseRequestFilters => {
    const parseFilterArray = (param: string | null): FilterValue[] => {
      if (!param) return [];
      return param.split(",").map((item) => {
        const [id, value] = item.split(":");
        return { id, value };
      });
    };

    const parseDateParam = (param: string | null): dayjs.Dayjs | undefined => {
      if (!param) return undefined;
      const date = dayjs(param);
      return date.isValid() ? date : undefined;
    };

    return {
      pr_created_by_admin_ids: parseFilterArray(
        searchParams.get("pr_created_by_admin_ids"),
      ),
      order_id: parseFilterArray(searchParams.get("order_id")),
      pr_status: parseFilterArray(searchParams.get("pr_status")),
      urgent_payment: parseFilterArray(
        searchParams.get("urgent_payment") ||
          (searchParams.get("is_urgent_sourcing_request") === "true"
            ? "true:Yes"
            : null),
      ),
      page_no: parseInt(searchParams.get("page_no") || "1", 10),
      search_text: searchParams.get("search_text") || undefined,
      limit: parseInt(searchParams.get("limit") || "30", 10),
      sort_by: searchParams.get("sort_by") || "created_date",
      sort_type:
        searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
      created_date_from: parseDateParam(searchParams.get("created_date_from")),
      created_date_to: parseDateParam(searchParams.get("created_date_to")),
      expected_payment_date_from: parseDateParam(
        searchParams.get("expected_payment_date_from"),
      ),
      expected_payment_date_to: parseDateParam(
        searchParams.get("expected_payment_date_to"),
      ),
      pending_approval_type: (() => {
        const fromParam = parsePurchaseRequestPendingApprovalTypes(
          searchParams.get("pending_approval_type"),
        );
        if (fromParam.length > 0) return fromParam;

        return [
          `${PurchaseRequestApprovalTeam.FINANCE}_pending`,
          `${PurchaseRequestApprovalTeam.SOURCING}_pending`,
          `${PurchaseRequestApprovalTeam.SWAGEAZY}_pending`,
        ].filter(
          (type): type is PurchaseRequestPendingApprovalType =>
            searchParams.get(type) === "true",
        );
      })(),
      approved_type: (() => {
        const fromParam = parsePurchaseRequestApprovedTypes(
          searchParams.get("approved_type"),
        );
        if (fromParam.length > 0) return fromParam;

        return [
          `${PurchaseRequestApprovalTeam.FINANCE}_approved`,
          `${PurchaseRequestApprovalTeam.SOURCING}_approved`,
          `${PurchaseRequestApprovalTeam.SWAGEAZY}_approved`,
        ].filter(
          (type): type is PurchaseRequestApprovedType =>
            searchParams.get(type) === "true",
        );
      })(),
    };
  };

  const initialParams: PurhcaseRequestFilters = parseUrlParams();

  const [filters, setFilters] = useState<PurhcaseRequestFilters>(initialParams);
  const [params, setParams] = useState<PurhcaseRequestFilters>(initialParams);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const appliedFilterCount = filterFields.reduce((count, field) => {
    const value = params[field];
    if (Array.isArray(value) && value.length > 0) {
      return count + 1;
    }
    if (
      value &&
      (field === "created_date_from" ||
        field === "expected_payment_date_from")
    ) {
      return count + 1;
    }
    return count;
  }, 0);

  const adminList: FilterValue[] = (TaskTrackerStore.admins ?? []).map(
    (admin: Admin) => ({
      id: String(admin.id),
      value: admin.name,
    }),
  );

  const orderIdList: FilterValue[] = (orderStore.orderIds ?? []).map((id) => ({
    id: String(id),
    value: String(id),
  }));

  const handleAdminSearch = (searchText?: string) => {
    if (adminSearchTimerRef.current) {
      clearTimeout(adminSearchTimerRef.current);
    }
    if (!searchText || searchText.length <= 2) return;
    adminSearchTimerRef.current = setTimeout(() => {
      TaskTrackerStore.getAdminBySearch(searchText).catch((err: unknown) => {
        let apiMessage: string | undefined;
        if (err && typeof err === "object" && "response" in err) {
          const data = (err as { response?: { data?: { message?: string } } })
            .response?.data;
          if (typeof data?.message === "string") {
            apiMessage = data.message;
          }
        }
        showError("Error", apiMessage || "Failed to search admins.");
      });
    }, 400);
  };

  const handleOrderSearch = (searchText?: string) => {
    if (orderSearchTimerRef.current) {
      clearTimeout(orderSearchTimerRef.current);
    }
    if (!searchText || searchText.length === 0) return;
    orderSearchTimerRef.current = setTimeout(() => {
      orderStore.searchOrderIds(searchText).catch(() => {
        showError("Error", "Failed to search orders.");
      });
    }, 400);
  };

  const handleFilterChange = useCallback(
    (
      key: keyof PurhcaseRequestFilters,
      value: PurhcaseRequestFilters[keyof PurhcaseRequestFilters],
    ) => {
      setFilters((prev: PurhcaseRequestFilters) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const handleApplyFilters = () => {
    const trimmedSearch = searchTerm.trim();
    setSearchTerm(trimmedSearch);
    setParams({
      ...filters,
      search_text: trimmedSearch || undefined,
      page_no: 1,
    });
  };

  const handleClearFilters = () => {
    const clearedFilters: PurhcaseRequestFilters = {
      pr_created_by_admin_ids: [],
      order_id: [],
      page_no: 1,
      search_text: undefined,
      limit: initialParams.limit,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      created_date_from: undefined,
      created_date_to: undefined,
      expected_payment_date_from: undefined,
      expected_payment_date_to: undefined,
      pending_approval_type: [],
      approved_type: [],
      pr_status: [],
      urgent_payment: [],
    };
    setSearchTerm("");
    setFilters(clearedFilters);
    setParams(clearedFilters);
  };

  const [isExporting, setIsExporting] = useState(false);
  const handleExport = () => {
    if (isExporting) return;
    setIsExporting(true);
    purchaseRequestStore
      .exportPurchaseRequests(params)
      .then((res) => {
        if (!res?.success) {
          showError("Error", res?.message || "Failed to start PR/PO export.");
          return;
        }
        showSuccess(
          res.message ||
            "PR/PO export job started. You will receive an email with the CSV file.",
        );
      })
      .catch((err: any) =>
        showError(
          "Error",
          err?.response?.data?.message || "Failed to start PR/PO export.",
        ),
      )
      .finally(() => setIsExporting(false));
  };

  useEffect(() => {
    if (canViewPrPo) {
      setIsLoading(true);
      purchaseRequestStore
        .getPurchaseRequestListing(params)
        .catch((err: any) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearchBar = (query: string | undefined) => {
    if (query && query.length > 0) {
      setSearchTerm(query);
    } else {
      setSearchTerm("");
      setParams((prev: PurhcaseRequestFilters) => ({
        ...prev,
        search_text: undefined,
        page_no: 1,
      }));
    }
  };

  const handleSearchSubmit = () => {
    const trimmedSearch = searchTerm.trim();
    setSearchTerm(trimmedSearch);
    setParams((prev: PurhcaseRequestFilters) => ({
      ...prev,
      search_text: trimmedSearch || undefined,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  useEffect(() => {
    purchaseRequestStore.prepareListingPage();
  }, [purchaseRequestStore]);

  useEffect(() => {
    if (purchaseRequestStore.isListingDebouncedSearchInitialMount) {
      purchaseRequestStore.completeListingDebouncedSearchInitialMount();
      return;
    }
    const trimmedSearch = debouncedSearchTerm.trim();
    setParams((prev: PurhcaseRequestFilters) => {
      if ((prev.search_text || "") === trimmedSearch) {
        return prev;
      }
      return {
        ...prev,
        search_text: trimmedSearch || undefined,
        page_no: 1,
      };
    });
  }, [
    debouncedSearchTerm,
    purchaseRequestStore,
    purchaseRequestStore.isListingDebouncedSearchInitialMount,
  ]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev: PurhcaseRequestFilters) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev: PurhcaseRequestFilters) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleSortChange = (column: string, sortType: SortType) => {
    setParams((prev: PurhcaseRequestFilters) => ({
      ...prev,
      sort_by: column,
      sort_type: sortType,
      page_no: 1,
    }));
  };

  const handleApprove = (prId: number, team: PurchaseRequestRejectedBy) => {
    setApprovePopup({ open: true, prId, team });
  };

  const handleApproveConfirm = (prId: number) => {
    const team = approvePopup.team!;
    purchaseRequestStore
      .approveRequest(prId, team, userStore)
      .then(() => {
        showSuccess("Request approved successfully");
        setApprovePopup({ open: false, prId: null, team: null });
      })
      .catch((err) => {
        showError(
          "Error",
          err.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
        setApprovePopup({ open: false, prId: null, team: null });
      });
  };

  const handleReject = (prId: number, team: PurchaseRequestRejectedBy) => {
    setRejectPopup({ open: true, prId, team });
  };

  const handleCancelPR = (prId: number) => {
    const pr = purchaseRequestStore.prlist.find((item) => item.id === prId);

    if (pr && pr.active_po === 1) {
      showError(
        "Error",
        "Unable to cancel the PR because it has an active PO assigned. Please close the PO before proceeding with the PR cancellation.",
      );
      return;
    }

    setCancelPRPopup({ open: true, prId });
  };

  const handleCancelPRConfirm = (prId: number, cancellationReason?: string) => {
    purchaseRequestStore
      .cancelPurchaseRequest(prId, userStore, cancellationReason)
      .then(() => {
        showSuccess("Request cancelled successfully");
        setCancelPRPopup({ open: false, prId: null });
      })
      .catch((err) => {
        showError(
          "Error",
          err.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
        setCancelPRPopup({ open: false, prId: null });
      })
      .finally(() => {
        setCancelPRPopup({ open: false, prId: null });
      });
  };

  const handleRejectConfirm = (prId: number, rejectionReason?: string) => {
    const team = rejectPopup.team!;
    purchaseRequestStore
      .rejectRequest(prId, team, userStore, rejectionReason)
      .then(() => {
        showSuccess("Request rejected successfully");
        setRejectPopup({ open: false, prId: null, team: null });
      })
      .catch((err) => {
        showError(
          "Error",
          err.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
        setRejectPopup({ open: false, prId: null, team: null });
      });
  };

  const handleGeneratePO = (prId: number) => {
    purchaseRequestStore
      .generatePO(prId)
      .then((response) => {
        showSuccess("PO generated successfully");
        purchaseRequestStore.prlist = purchaseRequestStore.prlist.map((pr) =>
          pr.id === prId
            ? {
                ...pr,
                po_ids: pr.po_ids
                  ? `${pr.po_ids} / ${response.result}`
                  : String(response.result),
              }
            : pr,
        );
      })
      .catch((err) => {
        showError(
          "Error",
          err.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (
            (key === "pending_approval_type" || key === "approved_type") &&
            value.length > 0
          ) {
            queryParams.append(key, value.join(","));
          } else if (
            value.length > 0 &&
            typeof value[0] === "object" &&
            "id" in value[0] &&
            "value" in value[0]
          ) {
            queryParams.append(
              key,
              value
                .map((item: FilterValue) => `${item.id}:${item.value}`)
                .join(","),
            );
          }
        } else if (
          key === "expected_payment_date_from" &&
          value &&
          dayjs.isDayjs(value)
        ) {
          if (value.isValid()) {
            queryParams.append(
              "expected_payment_date_from",
              value.toISOString(),
            );
          }
        } else if (
          key === "expected_payment_date_to" &&
          value &&
          dayjs.isDayjs(value)
        ) {
          if (value.isValid()) {
            queryParams.append("expected_payment_date_to", value.toISOString());
          }
        } else if (
          key === "created_date_from" &&
          value &&
          dayjs.isDayjs(value)
        ) {
          if (value.isValid()) {
            queryParams.append("created_date_from", value.toISOString());
          }
        } else if (key === "created_date_to" && value && dayjs.isDayjs(value)) {
          if (value.isValid()) {
            queryParams.append("created_date_to", value.toISOString());
          }
        } else if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();
      navigate(
        { search: queryString ? `?${queryString}` : "" },
        { replace: true },
      );
    };
    updateQueryParams();
  }, [params, navigate]);

  const handleActionsMenuOpen = () => {
    if (actionsAnchorRef.current) {
      setActionsMenuAnchor(actionsAnchorRef.current);
    }
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (actionId: string) => {
    handleActionsMenuClose();
    if (actionId === "export_csv") {
      handleExport();
      return;
    }
    if (
      actionId === "upload_vendor_payments_csv" &&
      userStore.hasPermission(EmployeeRoles.UPLOAD_VENDOR_PAYMENTS_CSV)
    ) {
      setVendorPaymentsCsvOpen(true);
    }
  };

  const isActionsMenuOpen = Boolean(actionsMenuAnchor);

  return (
    <>
      <Grid2 size={{ xs: 12 }} sx={{ px: 2 }}>
        {!canViewPrPo && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ ...modernPageCardSx(theme), mb: 2 }}>
              <CustomTypography variant="heading3">
                Purchase Requests
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage purchase requests and PO generation
              </CustomTypography>
            </Paper>
            <PermissionBlockLabel />
          </Box>
        )}

        {canViewPrPo && (
          <Paper sx={{ ...modernPageCardSx(theme), mt: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <CustomTypography variant="heading3">
                  Purchase Requests
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color={theme.palette.customColors?.greyTextColor}
                >
                  View and manage purchase requests and PO generation
                  {appliedFilterCount > 0
                    ? ` · ${appliedFilterCount} filter${appliedFilterCount === 1 ? "" : "s"} applied`
                    : ""}
                </CustomTypography>
              </Box>
              <Box ref={actionsAnchorRef}>
                <AppButton
                  label="Actions"
                  variant="grayscale"
                  onClick={handleActionsMenuOpen}
                  customStyles={{ minWidth: 140, minHeight: 40 }}
                />
              </Box>
            </Box>
            <Menu
              anchorEl={actionsMenuAnchor}
              open={isActionsMenuOpen}
              onClose={handleActionsMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.customColors.lightGray}`,
                    minWidth: 260,
                  },
                },
              }}
            >
              <MenuItem
                onClick={() => handleActionSelect("export_csv")}
                disabled={isExporting}
                sx={{ py: 1.25, px: 2 }}
              >
                <CustomTypography variant="body2" fontWeight={600}>
                  {isExporting ? "Exporting Data..." : "Export Data"}
                </CustomTypography>
              </MenuItem>
              {canUploadVendorPaymentsCsv ? (
                <MenuItem
                  onClick={() => handleActionSelect("upload_vendor_payments_csv")}
                  sx={{ py: 1.25, px: 2 }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Upload vendor payments CSV
                  </CustomTypography>
                </MenuItem>
              ) : null}
            </Menu>

            <Paper
              variant="outlined"
              sx={modernFilterPanelSx(theme)}
            >
              <Box
                onClick={() => purchaseRequestStore.toggleSearchExpanded()}
                sx={modernFilterPanelHeaderSx(
                  theme,
                  purchaseRequestStore.isSearchExpanded,
                )}
              >
                <CustomTypography variant="heading6">
                  Search & Filters
                </CustomTypography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    purchaseRequestStore.toggleSearchExpanded();
                  }}
                >
                  {purchaseRequestStore.isSearchExpanded ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </IconButton>
              </Box>
              <Collapse
                in={purchaseRequestStore.isSearchExpanded}
                timeout="auto"
                unmountOnExit
              >
                <Grid2 container spacing={2} columns={12} padding={2}>
                  <Grid2 size={{ xs: 12, sm: 9, md: 10, lg: 10 }}>
                    <FormTextField
                      marginBottom={0}
                      id="purchase-request-search"
                      placeholder="Search by PR Id, Order Id, PO Id, Vendor name, -1 for Swageazy Inventory"
                      type="search"
                      variant="outlined"
                      value={searchTerm}
                      onChange={(_, value) => handleSearchBar(value)}
                      onKeyDown={handleKeyDown}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 3, md: 2, lg: 2 }}>
                    <AppButton
                      variant="primary"
                      label="Search"
                      onClick={handleSearchSubmit}
                      customStyles={{ width: "100%", height: 40 }}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <DateRangePickerNew
                      placeholder="Created date range"
                      startDate={filters.created_date_from ?? null}
                      endDate={filters.created_date_to ?? null}
                      onRangeSelect={(range) => {
                        setFilters((prev: PurhcaseRequestFilters) => ({
                          ...prev,
                          created_date_from: range.startDate ?? undefined,
                          created_date_to: range.endDate ?? undefined,
                        }));
                      }}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Admin"
                      isMultiSelect
                      value={filters.pr_created_by_admin_ids ?? []}
                      list={adminList}
                      local={false}
                      handleSearch={handleAdminSearch}
                      handleSelect={(items: FilterValue[]) =>
                        handleFilterChange("pr_created_by_admin_ids", items)
                      }
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Order"
                      isMultiSelect
                      value={filters.order_id ?? []}
                      list={orderIdList}
                      local={false}
                      handleSearch={handleOrderSearch}
                      handleSelect={(items: FilterValue[]) =>
                        handleFilterChange("order_id", items)
                      }
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="PR Status"
                      isMultiSelect={false}
                      value={filters.pr_status ?? []}
                      list={PR_STATUS_OPTIONS}
                      local
                      handleSelect={(items: FilterValue[]) =>
                        handleFilterChange("pr_status", items)
                      }
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Urgent Payment"
                      isMultiSelect
                      value={filters.urgent_payment ?? []}
                      list={URGENT_PAYMENT_FILTER_OPTIONS}
                      local
                      handleSelect={(items: FilterValue[]) => {
                        handleFilterChange("urgent_payment", items);
                      }}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Pending Approval"
                      isMultiSelect
                      value={PURCHASE_REQUEST_PENDING_APPROVAL_OPTIONS.filter(
                        (option) =>
                          filters.pending_approval_type?.includes(
                            option.id as PurchaseRequestPendingApprovalType,
                          ),
                      )}
                      list={PURCHASE_REQUEST_PENDING_APPROVAL_OPTIONS}
                      local
                      handleSelect={(selectedValues: FilterValue[]) => {
                        handleFilterChange(
                          "pending_approval_type",
                          selectedValues.map(
                            (item) =>
                              item.id as PurchaseRequestPendingApprovalType,
                          ),
                        );
                      }}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Approved"
                      isMultiSelect
                      value={PURCHASE_REQUEST_APPROVED_OPTIONS.filter(
                        (option) =>
                          filters.approved_type?.includes(
                            option.id as PurchaseRequestApprovedType,
                          ),
                      )}
                      list={PURCHASE_REQUEST_APPROVED_OPTIONS}
                      local
                      handleSelect={(selectedValues: FilterValue[]) => {
                        handleFilterChange(
                          "approved_type",
                          selectedValues.map(
                            (item) => item.id as PurchaseRequestApprovedType,
                          ),
                        );
                      }}
                    />
                  </Grid2>

                  <Grid2
                    size={{ xs: 12 }}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={handleClearFilters}
                      customStyles={{ minWidth: 100 }}
                    />
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApplyFilters}
                      customStyles={{ minWidth: 100 }}
                    />
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>

            {isLoading && (
              <Container
                sx={{
                  minHeight: "40vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Container>
            )}

            {!isLoading && (
              <Box sx={{ mt: 2 }}>
                <PrListingTable
                  maxHeight={window.innerHeight - 380}
                  limit={params.limit || purchaseRequestStore.limit}
                  page_no={params.page_no || 1}
                  total_count={purchaseRequestStore.total_count}
                  total_pages={purchaseRequestStore.total_pages}
                  sortBy={params.sort_by}
                  sortType={params.sort_type}
                  data={purchaseRequestStore.prlist}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={handleSortChange}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onCancelPR={handleCancelPR}
                  onGeneratePO={handleGeneratePO}
                />
              </Box>
            )}
          </Paper>
        )}
      </Grid2>

      {/* Approve Confirmation Popup */}
      <ConfirmationPopup
        id={approvePopup.prId || 0}
        open={approvePopup.open}
        title="Approve Purchase Request"
        content="Are you sure you want to approve this purchase request? This action cannot be undone."
        type="standard"
        onConfirm={handleApproveConfirm}
        onCancel={() =>
          setApprovePopup({ open: false, prId: null, team: null })
        }
      />

      {/* Reject Confirmation Popup */}
      <ConfirmationPopup
        id={rejectPopup.prId || 0}
        open={rejectPopup.open}
        title="Reject Purchase Request"
        content="Are you sure you want to reject this purchase request? Please provide a reason for rejection."
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectPopup({ open: false, prId: null, team: null })}
      />

      <ConfirmationPopup
        id={cancelPRPopup.prId || 0}
        open={cancelPRPopup.open}
        title="Cancel Purchase Request"
        content="Are you sure you want to cancel this purchase request? Please provide a reason for cancellation."
        type="form"
        inputRequired={true}
        inputLabel="Cancellation Reason"
        inputType="text"
        onConfirm={handleCancelPRConfirm}
        onCancel={() => setCancelPRPopup({ open: false, prId: null })}
      />

      <VendorPaymentsCsvUploadDialog
        open={vendorPaymentsCsvOpen}
        onClose={() => setVendorPaymentsCsvOpen(false)}
        store={vendorPaymentsListingStore}
      />
    </>
  );
};
export default observer(PurchaseRequest);
